/* iter364 — DIL Audit Panel (Admin only).
 *
 * Shows the full DIL formula breakdown for the calling admin OR any
 * other user by email. Lets the admin paste historical DIL values
 * (124, 111, 100, 94) and back-solves which (weight, goal, cycle_phase)
 * combinations would have produced them.
 *
 * Mounted at /admin/dil-audit or accessible via Admin Panel.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity, ArrowLeft, Loader2, Search, FileText, History, FlaskConical,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";

export default function DilAuditPage() {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [emailQ, setEmailQ] = useState(user?.email || "");
  const [targets, setTargets] = useState("124,111,100,94");
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState("");

  const isAdmin = user?.role === "admin";

  const run = async (email) => {
    if (!email) return;
    setLoading(true);
    setError("");
    setAudit(null);
    try {
      const isOwn = email.toLowerCase() === (user?.email || "").toLowerCase();
      const path = isOwn
        ? `/dil/audit/me?targets=${encodeURIComponent(targets)}`
        : `/dil/audit/user/${encodeURIComponent(email)}?targets=${encodeURIComponent(targets)}`;
      const { data } = await axios.get(`${API}${path}`);
      setAudit(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Error";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) run(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const cardCls = isDark
    ? "bg-[hsl(222,47%,11%)] border-white/10 text-gray-100"
    : "bg-white border-gray-200 text-gray-900";
  const accent = isDark ? "text-fuchsia-300" : "text-fuchsia-600";

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,7%)]" : "bg-gradient-to-br from-fuchsia-50/30 via-white to-pink-50/30"}`}>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="dil-audit-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={isAdmin ? "/admin" : "/dashboard"} className={`p-2 -ml-2 rounded-lg hover:bg-black/5 ${isDark ? "hover:bg-white/5" : ""}`} data-testid="audit-back">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-fuchsia-500" />
                <h1 className="text-xl font-bold">{bg ? "DIL Audit" : "DIL Audit"}</h1>
              </div>
              <div className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg
                  ? "Защо личният DIL се промени? Пълен formula breakdown + back-solving на исторически стойности"
                  : "Why did personal DIL change? Full formula breakdown + back-solve historical values"}
              </div>
            </div>
          </div>
        </div>

        {/* Query form */}
        <div className={`rounded-2xl border ${cardCls} p-4 mb-4`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-2">
              <label className={`text-[10px] uppercase tracking-wider font-bold ${accent}`}>
                {bg ? "Email на потребител" : "User email"}
                {!isAdmin && <span className="ml-2 opacity-60 normal-case font-normal">{bg ? "(само вашия акаунт)" : "(your account only)"}</span>}
              </label>
              <input
                type="email"
                value={emailQ}
                onChange={(e) => setEmailQ(e.target.value)}
                disabled={!isAdmin}
                placeholder="user@example.com"
                className={`mt-1 w-full px-3 py-2 rounded-lg text-[13px] border focus:outline-none focus:ring-2 focus:ring-fuchsia-500
                  ${isDark ? "bg-white/5 border-white/10 text-white placeholder-gray-500" : "bg-white border-gray-200"}`}
                data-testid="audit-email-input"
              />
            </div>
            <div>
              <label className={`text-[10px] uppercase tracking-wider font-bold ${accent}`}>
                {bg ? "DIL стойности за обясняване" : "DIL values to explain"}
              </label>
              <input
                type="text"
                value={targets}
                onChange={(e) => setTargets(e.target.value)}
                placeholder="124,111,100,94"
                className={`mt-1 w-full px-3 py-2 rounded-lg text-[13px] border focus:outline-none focus:ring-2 focus:ring-fuchsia-500
                  ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-white border-gray-200"}`}
                data-testid="audit-targets-input"
              />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <Button
              onClick={() => run(emailQ)}
              disabled={loading || !emailQ}
              className="bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white"
              data-testid="audit-run"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
              {bg ? "Анализирай" : "Run audit"}
            </Button>
          </div>
          {error && <div className={`mt-2 text-[12px] rounded p-2 ${isDark ? "bg-red-500/10 text-red-300" : "bg-red-50 text-red-700"}`}>{error}</div>}
        </div>

        {/* Results */}
        {audit && (
          <div className="space-y-3">
            {/* Profile inputs */}
            <section className={`rounded-2xl border ${cardCls} p-4`} data-testid="audit-inputs">
              <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${accent}`}>
                {bg ? "Входни данни от профила" : "Profile inputs"}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[12px]">
                {Object.entries(audit.inputs).map(([k, v]) => (
                  <div key={k} className={`px-2 py-1 rounded ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <div className={`text-[9px] uppercase tracking-wider ${isDark ? "text-gray-400" : "text-gray-500"}`}>{k}</div>
                    <div className="font-mono">{v === null || v === undefined ? "—" : String(v)}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Step-by-step breakdown */}
            <section className={`rounded-2xl border ${cardCls} p-4`} data-testid="audit-breakdown">
              <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${accent}`}>
                {bg ? "Стъпка по стъпка изчисление" : "Step-by-step computation"}
              </div>
              <ol className="space-y-1 text-[12px]">
                {audit.breakdown.map((step, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className={`shrink-0 mt-0.5 w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold ${isDark ? "bg-fuchsia-500/20 text-fuchsia-300" : "bg-fuchsia-100 text-fuchsia-700"}`}>{i + 1}</span>
                    <span className="flex-1 font-mono leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
              {audit.computed?.personalized_dil && (
                <div className={`mt-3 p-3 rounded-lg text-center ${isDark ? "bg-fuchsia-500/10 ring-1 ring-fuchsia-500/30" : "bg-fuchsia-50 ring-1 ring-fuchsia-200"}`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">{bg ? "Личен DIL днес" : "Personal DIL today"}</div>
                  <div className="text-3xl font-bold">{audit.computed.personalized_dil} <span className="text-base font-normal opacity-70">g/{bg ? "ден" : "day"}</span></div>
                </div>
              )}
            </section>

            {/* Back-solved history */}
            {(audit.history_to_explain || []).length > 0 && (
              <section className={`rounded-2xl border ${cardCls} p-4`} data-testid="audit-history">
                <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${accent} flex items-center gap-1.5`}>
                  <History className="w-3.5 h-3.5" />
                  {bg ? "Обяснение на исторически стойности" : "Explaining historical values"}
                </div>
                <div className="space-y-2">
                  {audit.history_to_explain.map((entry) => (
                    <div key={entry.value} className={`rounded-lg p-2.5 ${isDark ? "bg-white/5" : "bg-gray-50"}`} data-testid={`audit-history-${entry.value}`}>
                      <div className="text-[13px] font-bold mb-1">{bg ? "DIL = " : "DIL = "}{entry.value} g/{bg ? "ден" : "day"}</div>
                      {entry.candidates.length === 0 && <div className={`text-[11px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>{bg ? "Няма подходящо обяснение" : "No matching explanation"}</div>}
                      {entry.candidates.map((c, i) => (
                        <div key={i} className={`text-[11px] mt-0.5 leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          <span className={`inline-block px-1.5 py-0.5 mr-1.5 rounded text-[9px] font-bold ${isDark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700"}`}>{c.type}</span>
                          {c.explanation_bg}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Scenarios at current weight */}
            {(audit.scenarios_at_current_weight || []).length > 0 && (
              <section className={`rounded-2xl border ${cardCls} p-4`} data-testid="audit-scenarios">
                <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${accent}`}>
                  {bg ? "Сценарии при текущо тегло" : "Scenarios at current weight"}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className={`text-left ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        <th className="py-1 pr-2 font-semibold">{bg ? "Цел" : "Goal"}</th>
                        <th className="py-1 pr-2 font-semibold text-right">carb/kg</th>
                        <th className="py-1 pr-2 font-semibold text-right">prot/kg</th>
                        <th className="py-1 font-semibold text-right">DIL g/{bg ? "ден" : "day"}</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {audit.scenarios_at_current_weight.map((s) => (
                        <tr key={s.goal} className={`${audit.computed.effective_goal === s.goal ? (isDark ? "bg-fuchsia-500/15" : "bg-fuchsia-50") : ""}`}>
                          <td className="py-1 pr-2">{bg ? s.label_bg : s.goal}</td>
                          <td className="py-1 pr-2 text-right">{s.carb_g_per_kg.toFixed(2)}</td>
                          <td className="py-1 pr-2 text-right">{s.protein_g_per_kg.toFixed(2)}</td>
                          <td className="py-1 text-right font-bold">{s.personalized_dil}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Formula reference */}
            <section className={`rounded-2xl border ${cardCls} p-4`} data-testid="audit-formula">
              <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${accent}`}>
                {bg ? "Формула (Nutramilo v2.0)" : "Formula (Nutramilo v2.0)"}
              </div>
              <div className={`font-mono text-[11px] p-2 rounded ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                DIL = bw_kg × (carb_g/kg + 0.69 × protein_g/kg) × cycle_modifier × estrogen_multiplier
              </div>
              <div className={`mt-2 text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                PMID: {(audit.computed.pmid || []).join(", ")} ·{" "}
                <a href={audit.formula_doc_url} target="_blank" rel="noopener noreferrer" className="underline">
                  {bg ? "Пълна методология (PyPI)" : "Full methodology (PyPI)"}
                </a>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
